import React, { useState } from 'react';
import { motion } from 'framer-motion';

function WhyAIPerformance({ title = "Our Performance Engineering Offerings", fData = [] }) {
    const [activeIndex, setActiveIndex] = useState(0);

    if (!fData || fData.length === 0) return null;

    return (
        <section className="relative w-full overflow-hidden bg-white pb-[60px] lg:pb-[90px]">
            <div className="relative z-10 mx-auto w-full max-w-[1440px] px-[20px] lg:px-[80px]">
                <div className="mb-[36px] lg:mb-[48px] flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <div className="max-w-[700px]">
                        <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-2.5">
                            From discovery to production
                        </p>
                        <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] leading-[1.2] text-[#01386A] tracking-tight">
                            {title}
                        </h2>
                    </div>

                    <div className="max-w-[480px]">
                        <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#49577A] leading-[1.6]">
                            The schedule below is an indicative delivery approach. Final timing depends on scope, integrations, data readiness, and security review.
                        </p>
                    </div>
                </div>

                {/* ================= HORIZONTAL ACCORDION ================= */}
                <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 items-stretch min-h-[480px] lg:h-[480px]">
                    {fData.map((item, index) => {
                        const isActive = activeIndex === index;
                        const stepNum = String(index + 1).padStart(2, '0');

                        return (
                            <motion.div
                                key={item.id || index}
                                onClick={() => setActiveIndex(index)}
                                onMouseEnter={() => setActiveIndex(index)}
                                layout
                                transition={{ duration: 0.45, ease: [0.25, 1, 0.5, 1] }}
                                className={`relative cursor-pointer overflow-hidden transition-all duration-300 ${isActive
                                    ? "flex-1 lg:flex-[3.5] bg-[#01386A] text-white shadow-xl"
                                    : "flex-none lg:flex-1 bg-white border border-[#DCE3E9] hover:border-[#01386A]/40"
                                    }`}
                            >
                                {isActive ? (
                                    /* ACTIVE EXPANDED CARD */
                                    <div className="flex flex-col h-full justify-between">
                                        {/* Top Image Banner */}
                                        <div className="relative w-full h-[220px] sm:h-[240px] overflow-hidden bg-[#0a1630]">
                                            <img
                                                src={item.image || "/assets/services/bg3.png"}
                                                alt={item.title}
                                                className="w-full h-full object-cover opacity-90 transition-transform duration-700 hover:scale-105"
                                            />
                                        </div>

                                        {/* Content Box */}
                                        <div className="p-6 sm:p-8 flex flex-col justify-between flex-1 relative">
                                            {/* Watermark Step Number */}
                                            <span className="absolute top-6 right-8 font-sora text-[20px] sm:text-[30px] font-[600] text-white/25 select-none pointer-events-none">
                                                {stepNum}
                                            </span>

                                            <div>
                                                <h3 className="font-sora text-[16px] sm:text-[20px] font-[600] text-white mb-4 leading-tight">
                                                    {item.title}
                                                </h3>
                                                <p className="font-graphik text-[14px] lg:text-[16px] font-[500] leading-[1.65] text-white/85 max-w-[540px]">
                                                    {item.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    /* INACTIVE COLLAPSED PILLAR */
                                    <div className="flex lg:flex-col items-center justify-between h-full p-5 sm:p-6 lg:py-8 lg:px-4">
                                        {/* Step Number */}
                                        <span className="font-sora text-[22px] sm:text-[26px] font-medium text-[#49577A]/70">
                                            {stepNum}
                                        </span>

                                        {/* Center Divider Line */}
                                        <div className="hidden lg:block w-[1px] h-[140px] bg-[#DCE3E9]" />

                                        {/* Vertical Bottom Label */}
                                        <div className="lg:[writing-mode:vertical-rl] lg:rotate-180 flex items-center justify-center">
                                            <span className="font-sora text-[15px] sm:text-[17px] font-bold text-[#01386A] tracking-wide whitespace-nowrap">
                                                {item.title}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>

            </div>
        </section>
    );
}

export default WhyAIPerformance;